import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Trash2, UserPlus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { collaborateursService } from '@/services';

interface AOPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aoId: string;
  aoTitle: string;
}

interface Collaborateur {
  id: string;
  nom: string;
  email: string;
  userId: string;
  estActif: boolean;
  userStatus?: 'active' | 'banned' | 'unverified';
}

interface Permission {
  id: string;
  userId: string;
  permissionType: 'read' | 'write' | 'admin';
  collaborateur?: Collaborateur;
}

export function AOPermissionsDialog({ open, onOpenChange, aoId, aoTitle }: AOPermissionsDialogProps) {
  const [collaborateurs, setCollaborateurs] = useState<Collaborateur[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState<'read' | 'write'>('read');
  const [loading, setLoading] = useState(false);
  const [excludedCount, setExcludedCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, aoId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les collaborateurs avec compte utilisable.
      const collabData = await collaborateursService.getAll();
      const collabsWithUsers = collabData
        .filter(c => c.userId)
        .map(c => ({
          id: c.id,
          nom: c.nom,
          email: c.email || '',
          userId: c.userId!,
          estActif: c.estActif,
          userStatus: c.userStatus
        }));
      const activeCollabsWithUsers = collabsWithUsers.filter(
        c => c.estActif && c.userStatus === 'active'
      );

      // Collaborateurs sans compte utilisable (pas d'email, ou compte bloqué)
      const excluded = collabData.filter(
        c => !c.userId || !c.estActif || c.userStatus !== 'active'
      ).length;
      setExcludedCount(excluded);

      // Charger les permissions existantes pour cet AO
      const { data: permResponse } = await api.get(`/appels-offres/${aoId}/permissions`);
      const permData = permResponse?.data?.permissions || [];

      // Enrichir les permissions avec les infos des collaborateurs
      const enrichedPermissions: Permission[] = permData.map((perm: any) => ({
        id: perm.id,
        userId: perm.userId,
        permissionType: perm.permissionType,
        collaborateur: collabsWithUsers.find(c => c.userId === perm.userId)
      }));

      setCollaborateurs(activeCollabsWithUsers);
      setPermissions(enrichedPermissions);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPermission = async () => {
    if (!selectedUserId || !selectedAccessLevel) return;

    try {
      await api.post(`/appels-offres/${aoId}/permissions`, {
        userId: selectedUserId,
        permissionType: selectedAccessLevel
      });

      toast({
        title: "Succès",
        description: "Permission ajoutée avec succès",
      });

      setSelectedUserId('');
      setSelectedAccessLevel('read');
      loadData();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la permission:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter la permission",
        variant: "destructive",
      });
    }
  };

  const handleRemovePermission = async (permissionId: string) => {
    try {
      await api.delete(`/appels-offres/${aoId}/permissions/${permissionId}`);

      toast({
        title: "Succès",
        description: "Permission supprimée avec succès",
      });

      loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression de la permission:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la permission",
        variant: "destructive",
      });
    }
  };

  const availableCollaborateurs = collaborateurs.filter(
    c => !permissions.some(p => p.userId === c.userId)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gestion des accès - {aoTitle}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {excludedCount > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {excludedCount} collaborateur{excludedCount > 1 ? 's' : ''} sans compte actif n'apparaissent pas. Ajoutez-leur un email et activez-les dans <strong>Gestion des collaborateurs</strong> pour pouvoir leur attribuer un accès.
              </AlertDescription>
            </Alert>
          )}
          {/* Ajouter une nouvelle permission */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Ajouter un accès
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Collaborateur</label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un collaborateur" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCollaborateurs.map((collab) => (
                        <SelectItem key={collab.id} value={collab.userId}>
                          {collab.nom} ({collab.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Niveau d'accès</label>
                  <Select value={selectedAccessLevel} onValueChange={(value) => setSelectedAccessLevel(value as 'read' | 'write')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read">Lecture seule</SelectItem>
                      <SelectItem value="write">Lecture et écriture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                onClick={handleAddPermission}
                disabled={!selectedUserId || loading}
                className="w-full"
              >
                Ajouter l'accès
              </Button>
            </CardContent>
          </Card>

          {/* Liste des permissions existantes */}
          <Card>
            <CardHeader>
              <CardTitle>Accès actuels</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Chargement...</p>
              ) : permissions.length === 0 ? (
                <p className="text-muted-foreground">Aucun accès configuré</p>
              ) : (
                <div className="space-y-3">
                  {permissions.map((permission) => (
                    <div key={permission.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{permission.collaborateur?.nom}</p>
                          <p className="text-sm text-muted-foreground">{permission.collaborateur?.email}</p>
                        </div>
                        <Badge variant={permission.permissionType === 'write' || permission.permissionType === 'admin' ? 'default' : 'secondary'}>
                          {permission.permissionType === 'admin' ? 'Admin' :
                           permission.permissionType === 'write' ? 'Lecture et écriture' : 'Lecture seule'}
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemovePermission(permission.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
